import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { AppShell } from "@/components/app-shell";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { format } from "date-fns";
import { cn } from "@/lib/utils";
import { toast } from "sonner";
import {
  ArrowLeft,
  ArrowRight,
  CalendarIcon,
  CheckCircle2,
  Info,
  Megaphone,
  Phone,
  MessageCircle,
  Eye,
  MousePointerClick,
  ClipboardList,
  Image as ImageIcon,
  Video,
  Rocket,
  ExternalLink,
  Sparkles,
} from "lucide-react";
import { usePaymentStatus } from "@/hooks/use-payment-status";
import { PaymentOverdueBanner } from "@/components/payment-overdue-banner";

export const Route = createFileRoute("/_authenticated/campaigns/new")({
  component: WizardPage,
});

type Objective =
  | "whatsapp"
  | "call"
  | "brand_awareness"
  | "traffic"
  | "lead_gen"
  | "";

const OBJECTIVES: {
  id: Objective;
  label: string;
  desc: string;
  icon: React.ComponentType<{ className?: string }>;
}[] = [
  { id: "whatsapp", label: "WhatsApp Message Ads", desc: "Drive chats to WhatsApp", icon: MessageCircle },
  { id: "call", label: "Call Ads", desc: "Get phone calls from prospects", icon: Phone },
  { id: "brand_awareness", label: "Brand Awareness Ads", desc: "Maximize reach & recall", icon: Eye },
  { id: "traffic", label: "Traffic Ads", desc: "Send visitors to a URL", icon: MousePointerClick },
  { id: "lead_gen", label: "Lead Generation Ads", desc: "Collect leads in-platform", icon: ClipboardList },
];

const STEPS = [
  { n: 1, title: "Campaign Basics" },
  { n: 2, title: "Ad Creatives" },
  { n: 3, title: "Destination" },
  { n: 4, title: "Review & Launch" },
];

function WizardPage() {
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const { isBlocked } = usePaymentStatus();

  // Step 1
  const [name, setName] = useState("");
  const [objective, setObjective] = useState<Objective>("");
  const [endDate, setEndDate] = useState<Date | undefined>();

  // Step 2
  const [format_, setFormat_] = useState<"image" | "video" | "">("");
  const [creativeLink, setCreativeLink] = useState("");
  const [headline, setHeadline] = useState("");
  const [caption, setCaption] = useState("");

  // Step 3
  const [websiteCta, setWebsiteCta] = useState("");
  const [websiteUrl, setWebsiteUrl] = useState("");
  const [leadCta, setLeadCta] = useState("");
  const [phone, setPhone] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  const step1Valid = name.trim() && objective && endDate;
  const step2Valid = format_ && creativeLink.trim() && headline.trim() && caption.trim();
  const step3Valid = (() => {
    if (objective === "whatsapp") return true;
    if (objective === "brand_awareness") return true;
    if (objective === "traffic") return websiteCta && websiteUrl.trim();
    if (objective === "lead_gen") return !!leadCta;
    if (objective === "call") return phone.trim().length >= 6;
    return false;
  })();

  function next() {
    if (step === 1 && !step1Valid) return;
    if (step === 2 && !step2Valid) return;
    if (step === 3 && !step3Valid) return;
    setStep((s) => Math.min(4, s + 1));
  }
  function back() {
    setStep((s) => Math.max(1, s - 1));
  }

  async function launch() {
    if (isBlocked) {
      toast.error("Payment overdue", {
        description: "Please settle your invoice to launch new campaigns.",
      });
      return;
    }
    setIsSubmitting(true);
    try {
      const { data: { user } } = await supabase.auth.getUser();

      const OBJECTIVE_LABELS: Record<Exclude<Objective, "">, string> = {
        whatsapp: "WhatsApp Message Ads",
        call: "Call Ads",
        brand_awareness: "Brand Awareness Ads",
        traffic: "Traffic Ads",
        lead_gen: "Lead Generation Ads",
      };
      const FORMAT_LABELS = { image: "Image Ad", video: "Video Ad" } as const;

      const formatMDY = (d: Date) =>
        `${d.getMonth() + 1}/${d.getDate()}/${d.getFullYear()}`;
      const pad = (n: number) => String(n).padStart(2, "0");
      const formatMDYHMS = (d: Date) =>
        `${formatMDY(d)} ${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`;

      const payload = {
        name,
        objective: objective ? OBJECTIVE_LABELS[objective] : "",
        end_date: endDate ? formatMDY(endDate) : "",
        submitted_at: formatMDYHMS(new Date()),
        format: format_ ? FORMAT_LABELS[format_] : "",
        creative_link: creativeLink,
        headline,
        caption,
        user_id: user?.id,
        user_email: user?.email,
        destination: {
          whatsapp_cta: objective === "whatsapp" ? "WhatsApp Message" : undefined,
          website_cta: objective === "traffic" ? websiteCta : undefined,
          website_url: objective === "traffic" ? websiteUrl : undefined,
          lead_cta: objective === "lead_gen" ? leadCta : undefined,
          phone: objective === "call" ? phone : undefined,
        },
      };

      const objectiveLabel = objective ? OBJECTIVE_LABELS[objective] : "";

      const [webhookRes, insertRes] = await Promise.all([
        fetch("https://n8n2.nexaeo.com/webhook/new-campaign", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(payload),
        }),
        user
          ? supabase.from("campaigns").insert({
              user_id: user.id,
              ads_name: name,
              objective: objectiveLabel,
              ...(endDate
                ? {
                    end_date: `${endDate.getFullYear()}-${pad(endDate.getMonth() + 1)}-${pad(endDate.getDate())}`,
                  }
                : {}),
            } as any)
          : Promise.resolve({ error: null } as { error: null }),
      ]);

      if (!webhookRes.ok) {
        throw new Error(`Webhook returned ${webhookRes.status}`);
      }
      if ("error" in insertRes && insertRes.error) {
        console.error("Campaign DB insert failed:", insertRes.error);
      }

      toast.success("Campaign submitted successfully");
      setTimeout(() => navigate({ to: "/campaigns" }), 600);
    } catch (error) {
      toast.error("Submission failed", {
        description: error instanceof Error ? error.message : "Please try again.",
      });
    } finally {
      setIsSubmitting(false);
    }
  }

  return (
    <AppShell title="Launch Campaign">
      <div className="mx-auto max-w-3xl space-y-6">
        {isBlocked && <PaymentOverdueBanner />}
        {/* Welcome banner */}
        <section className="surface-panel relative overflow-hidden p-6">
          <div
            aria-hidden
            className="absolute -top-20 -right-20 h-56 w-56 rounded-full opacity-50 blur-3xl"
            style={{ background: "var(--gradient-hero)" }}
          />
          <div className="relative">
            <div className="flex items-center gap-2 text-xs uppercase tracking-widest text-muted-foreground">
              <Sparkles className="h-3.5 w-3.5" /> Ad Launch Portal
            </div>
            <h1 className="mt-2 text-2xl md:text-3xl font-semibold text-gradient">
              Welcome to the NexAEO Ad Launch Portal
            </h1>
            <p className="mt-2 text-sm text-muted-foreground max-w-2xl">
              Please provide the necessary details below to initiate your new Meta Ads campaign.
            </p>

            <div className="mt-4 flex items-start gap-3 rounded-md border border-warning/30 bg-warning/10 p-3 text-sm">
              <Info className="h-4 w-4 mt-0.5 text-warning" />
              <p className="text-foreground/90">
                📌 <strong>Important:</strong> Before submitting your request, please read our
                campaign guidelines{" "}
                <a href="#" className="underline underline-offset-4 text-warning">
                  here
                </a>
                . For any urgent inquiries, please contact our support team.
              </p>
            </div>
          </div>
        </section>

        {/* Stepper */}
        <div className="flex items-center justify-between gap-2">
          {STEPS.map((s, i) => {
            const done = step > s.n;
            const active = step === s.n;
            return (
              <div key={s.n} className="flex-1 flex items-center gap-2">
                <div className="flex items-center gap-2 min-w-0">
                  <div
                    className={cn(
                      "h-7 w-7 shrink-0 rounded-full grid place-items-center text-[11px] font-semibold border",
                      done
                        ? "bg-success text-success-foreground border-success"
                        : active
                          ? "hero-gradient text-primary-foreground border-transparent"
                          : "border-border text-muted-foreground",
                    )}
                  >
                    {done ? <CheckCircle2 className="h-3.5 w-3.5" /> : s.n}
                  </div>
                  <span
                    className={cn(
                      "hidden sm:inline text-xs truncate",
                      active ? "text-foreground" : "text-muted-foreground",
                    )}
                  >
                    {s.title}
                  </span>
                </div>
                {i < STEPS.length - 1 && <div className="flex-1 h-px bg-border" />}
              </div>
            );
          })}
        </div>

        <div className="surface-panel p-6 md:p-8">
          {step === 1 && (
            <div className="space-y-6">
              <SectionTitle icon={Megaphone} title="Campaign Basics" />

              <div className="space-y-1.5">
                <Label htmlFor="name">Ads name</Label>
                <Input
                  id="name"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="e.g. Spring Launch — Lead Gen"
                />
              </div>

              <div className="space-y-2">
                <Label>Select your ad objective</Label>
                <div className="grid sm:grid-cols-2 gap-3">
                  {OBJECTIVES.map((o) => {
                    const selected = objective === o.id;
                    return (
                      <button
                        type="button"
                        key={o.id}
                        onClick={() => setObjective(o.id)}
                        className={cn(
                          "text-left rounded-lg border p-4 transition-all",
                          selected
                            ? "border-primary bg-primary/10 glow-ring"
                            : "border-border bg-background/40 hover:border-primary/40",
                        )}
                      >
                        <div className="flex items-start gap-3">
                          <div
                            className={cn(
                              "h-9 w-9 rounded-md grid place-items-center",
                              selected ? "hero-gradient" : "bg-accent/40",
                            )}
                          >
                            <o.icon
                              className={cn(
                                "h-4 w-4",
                                selected ? "text-primary-foreground" : "text-muted-foreground",
                              )}
                            />
                          </div>
                          <div className="min-w-0">
                            <div className="text-sm font-medium">{o.label}</div>
                            <div className="text-xs text-muted-foreground">{o.desc}</div>
                          </div>
                        </div>
                      </button>
                    );
                  })}
                </div>
              </div>

              <div className="space-y-1.5">
                <Label>Set end date</Label>
                <Popover>
                  <PopoverTrigger asChild>
                    <Button
                      variant="outline"
                      className={cn(
                        "w-full justify-start text-left font-normal h-10",
                        !endDate && "text-muted-foreground",
                      )}
                    >
                      <CalendarIcon className="h-4 w-4" />
                      {endDate ? format(endDate, "PPP") : <span>Pick an end date</span>}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0" align="start">
                    <Calendar
                      mode="single"
                      selected={endDate}
                      onSelect={setEndDate}
                      disabled={(d) => d < new Date()}
                      initialFocus
                      className={cn("p-3 pointer-events-auto")}
                    />
                  </PopoverContent>
                </Popover>
              </div>
            </div>
          )}

          {step === 2 && (
            <div className="space-y-6">
              <SectionTitle icon={ImageIcon} title="Ad Creatives & Copy" />

              <div className="space-y-2">
                <Label>Ad format</Label>
                <div className="grid grid-cols-2 gap-3">
                  {[
                    { id: "image", label: "Image Ad", icon: ImageIcon },
                    { id: "video", label: "Video Ad", icon: Video },
                  ].map((f) => {
                    const selected = format_ === f.id;
                    return (
                      <button
                        type="button"
                        key={f.id}
                        onClick={() => setFormat_(f.id as "image" | "video")}
                        className={cn(
                          "rounded-lg border p-4 flex flex-col items-center gap-2 transition-all",
                          selected
                            ? "border-primary bg-primary/10 glow-ring"
                            : "border-border bg-background/40 hover:border-primary/40",
                        )}
                      >
                        <f.icon
                          className={cn(
                            "h-5 w-5",
                            selected ? "text-primary" : "text-muted-foreground",
                          )}
                        />
                        <span className="text-sm font-medium">{f.label}</span>
                      </button>
                    );
                  })}
                </div>
              </div>

              <div className="space-y-1.5">
                <Label htmlFor="creative">Ad creative link</Label>
                <Input
                  id="creative"
                  value={creativeLink}
                  onChange={(e) => setCreativeLink(e.target.value)}
                  placeholder="https://drive.google.com/..."
                />
                <p className="text-xs text-muted-foreground">
                  To publish your ad, upload your selected image or video to Google Drive, generate
                  a shareable link, and paste it here.
                </p>
              </div>

              <div className="space-y-1.5">
                <Label htmlFor="headline">Headline</Label>
                <Input
                  id="headline"
                  value={headline}
                  onChange={(e) => setHeadline(e.target.value)}
                  placeholder="A short, punchy line"
                />
              </div>

              <div className="space-y-1.5">
                <Label htmlFor="caption">Ad caption</Label>
                <Textarea
                  id="caption"
                  rows={5}
                  value={caption}
                  onChange={(e) => setCaption(e.target.value)}
                  placeholder="Write your primary ad copy here…"
                />
              </div>
            </div>
          )}

          {step === 3 && (
            <div className="space-y-6">
              <SectionTitle icon={ExternalLink} title="Destination Details" />

              {objective === "whatsapp" && (
                <div className="space-y-2">
                  <Label>WhatsApp CTA Button</Label>
                  <RadioGroup value="whatsapp_message">
                    <CtaRadio value="whatsapp_message" label="WhatsApp Message" checked />
                  </RadioGroup>
                  <p className="text-xs text-muted-foreground">
                    Pre-selected based on your campaign objective.
                  </p>
                </div>
              )}

              {objective === "traffic" && (
                <>
                  <div className="space-y-2">
                    <Label>Website CTA Button</Label>
                    <RadioGroup value={websiteCta} onValueChange={setWebsiteCta} className="grid grid-cols-2 gap-2">
                      {["Learn More", "Apply Now", "Sign Up", "Shop Now"].map((c) => (
                        <CtaRadio key={c} value={c} label={c} checked={websiteCta === c} />
                      ))}
                    </RadioGroup>
                  </div>
                  <div className="space-y-1.5">
                    <Label htmlFor="url">Website / Destination URL</Label>
                    <Input
                      id="url"
                      value={websiteUrl}
                      onChange={(e) => setWebsiteUrl(e.target.value)}
                      placeholder="https://example.com/landing"
                    />
                  </div>
                </>
              )}

              {objective === "lead_gen" && (
                <div className="space-y-2">
                  <Label>Lead Form CTA Button</Label>
                  <RadioGroup value={leadCta} onValueChange={setLeadCta} className="grid grid-cols-3 gap-2">
                    {["Get Offer", "Get Quote", "Shop Now"].map((c) => (
                      <CtaRadio key={c} value={c} label={c} checked={leadCta === c} />
                    ))}
                  </RadioGroup>
                </div>
              )}

              {objective === "call" && (
                <div className="space-y-1.5">
                  <Label htmlFor="phone">Mobile number</Label>
                  <Input
                    id="phone"
                    value={phone}
                    onChange={(e) => setPhone(e.target.value)}
                    placeholder="+1 555 123 4567"
                  />
                </div>
              )}

              {objective === "brand_awareness" && (
                <div className="flex items-start gap-3 rounded-md border border-border bg-accent/20 p-4 text-sm">
                  <Info className="h-4 w-4 mt-0.5 text-muted-foreground" />
                  <p>No destination details required for Brand Awareness Ads.</p>
                </div>
              )}
            </div>
          )}

          {step === 4 && (
            <div className="space-y-6">
              <SectionTitle icon={Rocket} title="Review & Launch" />

              <div className="rounded-lg border border-border bg-background/40 divide-y divide-border text-sm">
                <SummaryRow label="Ads name" value={name} />
                <SummaryRow
                  label="Objective"
                  value={OBJECTIVES.find((o) => o.id === objective)?.label || ""}
                />
                <SummaryRow label="End date" value={endDate ? format(endDate, "PPP") : ""} />
                <SummaryRow label="Format" value={format_ === "image" ? "Image Ad" : "Video Ad"} />
                <SummaryRow label="Creative link" value={creativeLink} mono />
                <SummaryRow label="Headline" value={headline} />
                <SummaryRow label="Caption" value={caption} multi />

                {objective === "whatsapp" && (
                  <SummaryRow label="CTA" value="WhatsApp Message" />
                )}
                {objective === "traffic" && (
                  <>
                    <SummaryRow label="Website CTA" value={websiteCta} />
                    <SummaryRow label="Destination URL" value={websiteUrl} mono />
                  </>
                )}
                {objective === "lead_gen" && (
                  <SummaryRow label="Lead Form CTA" value={leadCta} />
                )}
                {objective === "call" && <SummaryRow label="Mobile number" value={phone} />}
                {objective === "brand_awareness" && (
                  <SummaryRow label="Destination" value="N/A (Brand Awareness)" />
                )}
              </div>

              <Button variant="hero" size="xl" className="w-full" onClick={launch} disabled={isSubmitting || isBlocked}>
                <Rocket /> {isBlocked ? "Launch disabled — Payment overdue" : isSubmitting ? "Submitting…" : "Launch Campaign"}
              </Button>
            </div>
          )}

          {/* Nav controls */}
          <div className="mt-8 flex items-center justify-between">
            <Button variant="ghost" onClick={back} disabled={step === 1}>
              <ArrowLeft /> Back
            </Button>
            {step < 4 && (
              <Button
                variant="hero"
                onClick={next}
                disabled={
                  (step === 1 && !step1Valid) ||
                  (step === 2 && !step2Valid) ||
                  (step === 3 && !step3Valid)
                }
              >
                Continue <ArrowRight />
              </Button>
            )}
          </div>
        </div>
      </div>
    </AppShell>
  );
}

function SectionTitle({
  icon: Icon,
  title,
}: {
  icon: React.ComponentType<{ className?: string }>;
  title: string;
}) {
  return (
    <div className="flex items-center gap-2.5 pb-2 border-b border-border">
      <div className="h-8 w-8 rounded-md bg-accent/40 grid place-items-center">
        <Icon className="h-4 w-4 text-muted-foreground" />
      </div>
      <h2 className="text-lg font-semibold">{title}</h2>
    </div>
  );
}

function CtaRadio({
  value,
  label,
  checked,
}: {
  value: string;
  label: string;
  checked: boolean;
}) {
  return (
    <label
      className={cn(
        "flex items-center gap-2 rounded-md border px-3 py-2.5 cursor-pointer transition",
        checked
          ? "border-primary bg-primary/10"
          : "border-border bg-background/40 hover:border-primary/40",
      )}
    >
      <RadioGroupItem value={value} />
      <span className="text-sm">{label}</span>
    </label>
  );
}

function SummaryRow({
  label,
  value,
  mono,
  multi,
}: {
  label: string;
  value: string;
  mono?: boolean;
  multi?: boolean;
}) {
  return (
    <div className="grid grid-cols-3 gap-3 px-4 py-3">
      <div className="text-xs uppercase tracking-wider text-muted-foreground">{label}</div>
      <div
        className={cn(
          "col-span-2",
          mono && "font-mono text-xs break-all",
          multi && "whitespace-pre-wrap",
        )}
      >
        {value || <span className="text-muted-foreground">—</span>}
      </div>
    </div>
  );
}
